/**
 * Settings and secrets that used to live only in `.env` (ADR 0011). Each has a choice of which value
 * counts — the one entered here, the `.env` one, or none (off / the default); a value entered here is kept
 * while another is chosen. Secrets are write-only: the server says whether one is set and where
 * from, never what it is.
 */
import { useCallback, useEffect, useState } from "react";

export type Source = "settings" | "env" | "unset";

export type Mode = "value" | "env" | "off";

export interface SiteEntry {
  name: string;
  secret: boolean;
  source: Source | "off";
  /** which value counts: entered here, from `.env`, or none (the default) */
  mode?: Mode;
  /** a value is stored here (kept while `.env` or off is chosen) */
  hasValue?: boolean;
  envSet: boolean;
  updatedAt: string | null;
  /** only for settings that are not secret */
  value?: string;
}

export interface SiteField extends SiteEntry {
  kind: "text" | "secret" | "enum" | "bool" | "int";
  options: string[];
  default: string;
  /** shown only while another field has one of these values */
  when: { name: string; values: string[] } | null;
}

export interface SiteSnapshot {
  groups: { id: string; fields: SiteField[] }[];
  secrets: SiteEntry[];
}

async function call<T>(method: string, query = "", body?: unknown): Promise<T> {
  const res = await fetch(`/api/internal/siteconf${query}`, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? "error.generic");
  return data;
}

export const loadSiteConf = () => call<SiteSnapshot>("GET");
export const putSiteValue = (name: string, value: string) =>
  call<{ field: SiteEntry }>("PUT", "", { name, value });
export const setSiteMode = (name: string, mode: Mode) =>
  call<{ field: SiteEntry }>("PUT", "", { name, mode });
export const clearSiteValue = (name: string) =>
  call<{ field: SiteEntry }>("DELETE", `?name=${encodeURIComponent(name)}`);

/** Names a secret may have: `FORGEJO_TOKEN`, `DRANG_AUTOMATION_…`, `DRANG_ICS_…` (mirrors the server rule). */
export function validSecretName(name: string): boolean {
  if (!/^[A-Z][A-Z0-9_]{1,63}$/.test(name) || name.startsWith("POSTGRES_")) return false;
  return !name.startsWith("DRANG_") || name.startsWith("DRANG_AUTOMATION_") || name.startsWith("DRANG_ICS_");
}

/** A default name for a connection's secret: `FORGEJO_CODEBERG_TOKEN`. */
export function secretNameFor(
  pluginId: string,
  connectionId: string,
  what: "TOKEN" | "WEBHOOK_SECRET",
): string {
  const part = (s: string) =>
    s
      .replace(/^tasktype-/, "")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  return [part(pluginId), part(connectionId), what].filter(Boolean).join("_");
}

/** The snapshot for admins (null while loading or for anyone else), and a way to reload it. */
export function useSiteConf(enabled: boolean) {
  const [snap, setSnap] = useState<SiteSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(
    () =>
      loadSiteConf().then(
        (s) => {
          setSnap(s);
          setError(null);
        },
        (e: Error) => setError(e.message),
      ),
    [],
  );
  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);
  return { snap, error, refresh };
}

export function entryOf(snap: SiteSnapshot | null, name: string): SiteEntry | undefined {
  if (!snap) return undefined;
  for (const g of snap.groups) for (const f of g.fields) if (f.name === name) return f;
  return snap.secrets.find((s) => s.name === name);
}
